import React, { ReactElement, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { ExpandChevron } from 'components/general';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormattedMessage, formatTranslation } from 'intl';
import { CommonTranslationKeys, WorkflowTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { findById } from 'shared/utils/id/findById';
import {
  WorkflowStageIcon,
  ApprovalType,
  getStageColorByIndex,
  hasApprovalRequired,
  getDefaultAutoApproveForApprovalType,
  isNonApprovalStage,
  isSequentialApproval,
} from 'shared/domain/workflow/types';
import type { LabelledUserWithSitesAndProcesses } from 'shared/domain/user/mapping/toView';
import { StageIconPicker, getStageIconComponent } from '../StageIconPicker';
import { ApproversListWithDragDrop } from '../ApproversListWithDragDrop';
import { WorkflowConfigurationTranslationKeys } from '../../workflowConfigurationTranslationKeys';
import type { StageRowProps } from './types';
import {
  accordionSx,
  accordionSummarySx,
  accordionDetailsSx,
  headerToggleIconSx,
  headerIconPrimarySx,
  getRowWrapperSx,
  getBadgeSx,
  badgeIconSx,
  badgeIndexSx,
  getTitleSx,
  approvalChipSx,
  getDragHandleSx,
} from './styles';

export type { StageRowProps } from './types';

const STAGE_APPROVAL_BADGE_KEY = {
  none: WorkflowTranslationKeys.stageApprovalNone,
  sequential: WorkflowTranslationKeys.stageApprovalSequential,
  parallel: WorkflowTranslationKeys.stageApprovalParallel,
} as const;

const CREATOR_APPROVAL_TOGGLE_LABEL_KEY = {
  enabled: WorkflowTranslationKeys.creatorApprovalEnabled,
  disabled: WorkflowTranslationKeys.creatorEnableApproval,
} as const;

export function StageRow({
  stage,
  stageIndex,
  totalStages,
  isLocked,
  disabled,
  approversDisabled,
  focusNameInput = false,
  showNameError = false,
  availableApprovers,
  onUpdate,
  onDelete,
}: StageRowProps): ReactElement {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(() => true);
  const [nameTouched, setNameTouched] = useState(false);

  const isFirstOrLastStage = stageIndex === 0 || stageIndex === totalStages - 1;
  const showApprovalTypeSelect = !isFirstOrLastStage;
  const showApprovers =
    !isFirstOrLastStage && hasApprovalRequired({ approvalType: stage.approvalType });
  const showDeadline = showApprovers;

  const trimmedName = stage.name.trim();
  const hasDisplayName = trimmedName.length > 0;
  const hasNameError = (showNameError || nameTouched) && trimmedName.length === 0;
  const accordionSummaryTitle = hasDisplayName
    ? stage.name
    : formatTranslation(intl, WorkflowTranslationKeys.creatorNewStageHeading);

  const approverUsers = useMemo<LabelledUserWithSitesAndProcesses[]>(() => {
    return stage.approvers
      .map((a) => findById(availableApprovers, a.userId))
      .filter((u): u is LabelledUserWithSitesAndProcesses => Boolean(u));
  }, [stage.approvers, availableApprovers]);

  useEffect(() => {
    if (isLocked) {
      setExpanded(showApprovers);
    } else {
      setExpanded(true);
    }
  }, [isLocked, showApprovers]);

  const isDragDisabled = disabled || isFirstOrLastStage;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: stage._localId,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const stageColor = getStageColorByIndex(stageIndex, totalStages);

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  const toggleExpanded = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(stage._localId, { name: e.target.value });
    },
    [onUpdate, stage._localId]
  );

  /**
   * Per-edit flag: n follows approvers.length until the user manually changes n.
   * Not persisted -- once the user picks a custom n, we stop auto-tracking.
   * For sequential stages we keep the legacy "every approver decides" semantics by
   * never exposing the n field, so autoFollow stays a no-op there.
   */
  const autoFollowRef = useRef<boolean>(stage.requiredApprovals === stage.approvers.length || stage.requiredApprovals === null);

  const handleApprovalTypeChange = useCallback(
    (newType: ApprovalType) => {
      const enabling = hasApprovalRequired({ approvalType: newType });
      autoFollowRef.current = true;
      onUpdate(stage._localId, {
        approvalType: newType,
        autoApprove: getDefaultAutoApproveForApprovalType(newType),
        approvers: enabling ? stage.approvers : [],
        requiredApprovals: enabling ? (stage.approvers.length > 0 ? stage.approvers.length : null) : null,
      });
    },
    [onUpdate, stage._localId, stage.approvers]
  );

  const handleToggleApproval = useCallback(() => {
    const enabling = isNonApprovalStage({ approvalType: stage.approvalType });
    // Preserve the previous approvalType (incl. legacy 'sequential' values on stages
    // created via the DB / earlier UI). When the user explicitly enables approval here,
    // we default to parallel; otherwise we round-trip the value untouched.
    if (enabling) {
      handleApprovalTypeChange(ApprovalType.PARALLEL);
    } else {
      handleApprovalTypeChange(ApprovalType.NONE);
    }
  }, [stage.approvalType, handleApprovalTypeChange]);

  const handleApproversChange = useCallback(
    (newApprovers: LabelledUserWithSitesAndProcesses[]) => {
      const nextCount = newApprovers.length;
      const currentN = stage.requiredApprovals;
      let nextRequired: number | null = currentN;
      if (nextCount === 0) {
        nextRequired = null;
      } else if (autoFollowRef.current) {
        nextRequired = nextCount;
      } else if (currentN === null || currentN > nextCount) {
        // Manual n was set, but the new approvers count can't satisfy it -- clamp.
        nextRequired = nextCount;
      }
      onUpdate(stage._localId, {
        approvers: newApprovers.map((u) => ({ userId: u._id })),
        requiredApprovals: nextRequired,
      });
    },
    [onUpdate, stage._localId, stage.requiredApprovals]
  );

  const handleRequiredApprovalsChange = useCallback(
    (raw: string) => {
      // Empty during editing (e.g. user cleared "3" to type "2") -- leave the
      // stored value untouched so we don't snap mid-stroke. The number input
      // already enforces min=1/max=approvers.length on commit; the onBlur
      // handler clamps any leftover empty state back to a valid number.
      if (raw === '') return;
      const parsed = parseInt(raw, 10);
      if (Number.isNaN(parsed)) return;
      const max = stage.approvers.length;
      const clamped = Math.max(1, Math.min(parsed, max || 1));
      autoFollowRef.current = clamped === max;
      onUpdate(stage._localId, { requiredApprovals: clamped });
    },
    [onUpdate, stage._localId, stage.approvers.length]
  );

  const handleRequiredApprovalsBlur = useCallback(
    (raw: string) => {
      // If the field is empty when focus leaves, clamp to the min (1). Without
      // this the input would render with an empty value while the row state
      // still holds the previous number.
      if (raw === '') {
        autoFollowRef.current = false;
        onUpdate(stage._localId, { requiredApprovals: 1 });
      }
    },
    [onUpdate, stage._localId]
  );

  const handleDeadlineDaysChange = useCallback(
    (raw: string) => {
      const next = raw === '' ? undefined : Math.max(0, parseInt(raw, 10) || 0);
      onUpdate(stage._localId, { deadlineDays: next });
    },
    [onUpdate, stage._localId]
  );

  const handleStageIconChange = useCallback(
    (nextIcon: WorkflowStageIcon | null) => {
      onUpdate(stage._localId, { icon: nextIcon });
    },
    [onUpdate, stage._localId]
  );

  const approvalBadgeMessageId =
    STAGE_APPROVAL_BADGE_KEY[
      isNonApprovalStage(stage)
        ? 'none'
        : isSequentialApproval(stage)
          ? 'sequential'
          : 'parallel'
    ];

  const approvalEnabled = hasApprovalRequired({ approvalType: stage.approvalType });
  const sequentialStage = isSequentialApproval(stage);

  const approvalToggleLabelKey =
    CREATOR_APPROVAL_TOGGLE_LABEL_KEY[approvalEnabled ? 'enabled' : 'disabled'];

  const showRequiredApprovalsField = approvalEnabled && !sequentialStage && stage.approvers.length > 0;

  const deadlineDaysFieldValue: number | '' =
    typeof stage.deadlineDays === 'number' ? stage.deadlineDays : '';

  const mainFieldsGridColumns = useMemo(() => {
    const iconCol = 'minmax(68px, 76px)';
    /** Takes all remaining horizontal space after fixed/auto siblings. */
    const nameCol = 'minmax(140px, 1fr)';
    /** Content-sized column so the stage name field can grow as much as possible. */
    const approvalCol = 'minmax(168px, auto)';
    const deadlineCol = 'minmax(96px, 132px)';

    if (!showApprovalTypeSelect) {
      return `${iconCol} ${nameCol}`;
    }
    if (!showDeadline) {
      return `${iconCol} ${nameCol} ${approvalCol}`;
    }
    return `${iconCol} ${nameCol} ${approvalCol} ${deadlineCol}`;
  }, [showApprovalTypeSelect, showDeadline]);

  return (
    <Box ref={setNodeRef} sx={getRowWrapperSx(style)}>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => {
          setExpanded(nextExpanded);
        }}
        sx={accordionSx}
        data-qa={`stage-row-${stage._localId}`}
      >
        <AccordionSummary expandIcon={false} sx={accordionSummarySx}>
          {!isLocked ? (
            <IconButton
              size="small"
              disabled={isDragDisabled}
              sx={getDragHandleSx(isDragDisabled)}
              onClick={(e) => e.stopPropagation()}
              {...(isDragDisabled ? {} : { ...attributes, ...listeners })}
            >
              <DragHandleIcon fontSize="small" />
            </IconButton>
          ) : null}

          <Box sx={getBadgeSx(stageColor)}>
            {stage.icon === null ? (
              <Typography component="span" sx={badgeIndexSx}>
                {stageIndex + 1}
              </Typography>
            ) : (
              React.createElement(getStageIconComponent(stage.icon), { sx: badgeIconSx })
            )}
          </Box>

          <Typography sx={getTitleSx(hasDisplayName)} noWrap>
            {accordionSummaryTitle}
          </Typography>

          {hasApprovalRequired({ approvalType: stage.approvalType }) ? (
            <Chip
              label={<FormattedMessage id={approvalBadgeMessageId} />}
              size="small"
              variant="filled"
              color="primary"
              sx={approvalChipSx}
            />
          ) : null}

          {!isLocked && !isFirstOrLastStage ? (
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              disabled={disabled}
              sx={headerIconPrimarySx}
              title={formatTranslation(intl, CommonTranslationKeys.delete)}
              aria-label={formatTranslation(intl, CommonTranslationKeys.delete)}
              data-qa={`stage-row-delete-${stage._localId}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}

          <IconButton
            size="small"
            sx={headerToggleIconSx}
            onClick={toggleExpanded}
            aria-expanded={expanded}
          >
            <ExpandChevron expanded={expanded} />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            ...accordionDetailsSx,
            pt: isLocked ? 4 : 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Stack spacing={2} useFlexGap sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: mainFieldsGridColumns },
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              <StageIconPicker
                layout="standard"
                value={stage.icon}
                onChange={handleStageIconChange}
                stageNumber={stageIndex + 1}
                disabled={disabled}
                fullWidth={false}
              />

              <Box sx={{ minWidth: 0, width: '100%' }}>
                <TextField
                  label={formatTranslation(intl, WorkflowTranslationKeys.creatorStageName)}
                  value={stage.name}
                  onChange={handleNameChange}
                  onBlur={() => setNameTouched(true)}
                  autoFocus={focusNameInput && !isLocked}
                  size="small"
                  disabled={disabled}
                  fullWidth
                  error={hasNameError}
                  helperText={
                    hasNameError
                      ? formatTranslation(intl, WorkflowConfigurationTranslationKeys.STAGE_NAME_REQUIRED )
                      : undefined
                  }
                />
              </Box>

              {showApprovalTypeSelect ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={approvalEnabled}
                      onChange={handleToggleApproval}
                      disabled={disabled || sequentialStage}
                      color="primary"
                      data-qa={`stage-row-toggle-approval-${stage._localId}`}
                    />
                  }
                  label={
                    <FormattedMessage
                      id={approvalToggleLabelKey}
                    />
                  }
                  sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                />
              ) : null}

              {showDeadline ? (
                <TextField
                  label={formatTranslation(intl, WorkflowTranslationKeys.creatorDeadlineDays)}
                  type="number"
                  value={deadlineDaysFieldValue}
                  onChange={(e) => handleDeadlineDaysChange(e.target.value)}
                  size="small"
                  disabled
                  fullWidth
                  inputProps={{ min: 0 }}
                  placeholder={formatTranslation(intl, WorkflowTranslationKeys.creatorDeadlineDaysPlaceholder,)}
                />
              ) : null}
            </Box>

            {showApprovers ? (
              <ApproversListWithDragDrop
                approvers={approverUsers}
                availableApprovers={availableApprovers}
                approvalType={stage.approvalType}
                onChange={handleApproversChange}
                disabled={approversDisabled}
                isLocked={isLocked}
              />
            ) : null}

            {showRequiredApprovalsField ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 1.5,
                  py: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
                data-qa={`stage-row-required-approvals-${stage._localId}`}
              >
                <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                  <FormattedMessage id={WorkflowTranslationKeys.creatorRequiredApprovalsLabel} />
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={stage.requiredApprovals ?? ''}
                  onChange={(e) => handleRequiredApprovalsChange(e.target.value)}
                  onBlur={(e) => handleRequiredApprovalsBlur(e.target.value)}
                  disabled={approversDisabled}
                  inputProps={{
                    min: 1,
                    max: stage.approvers.length,
                    'aria-label': formatTranslation(intl, WorkflowConfigurationTranslationKeys.REQUIRED_APPROVALS_LABEL,),
                    style: { textAlign: 'center', width: 56 },
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  <FormattedMessage
                    id={WorkflowTranslationKeys.creatorRequiredApprovalsOfTotal}
                    values={{ total: stage.approvers.length }}
                  />
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
